import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Typography, IconButton, Tooltip } from '@mui/material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { fetchDevicesAvailable } from '#action/device/device-avaliable';
import { fetchProjectAlerts } from '#action/project/alerts';
import {
  Title,
  StyledPaper,
  AddButtonWrapper,
  StyledAddIcon,
  ButtonContainer,
  StyledButton
} from './styles';
import DeviceItem from './device-item';
import { useIntersection } from '#hooks';
import { uuidv4 } from '#helper';

/**
 * Renders device items and error field for all items.
 * @returns {React.ReactFragment}
 */

const DeviceItems = props => {
  const {
    forceError, projectAlerts, projectId, applianceId, isApplianceStop, fields, remove
  } = props;
  const { formState: { errors }, setValue } = useFormContext();
  const rootMessage = errors?.devicesGroup?.root?.message;
  const groupMessage = errors?.devicesGroup?.message;
  const error = typeof rootMessage === 'string' ? rootMessage :
    (typeof groupMessage === 'string' ? groupMessage : undefined);

  const [expanded, setExpanded] = useState(fields?.length ? `devicesGroup[${fields.length - 1}]` : false);

  const handleSetExpanded = useCallback(panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  useEffect(() => {
    if (fields.length) {
      setExpanded(`devicesGroup[${fields.length - 1}]`);
    }
  }, [fields.length]);

  return (
    <>
      {fields.map((field, index, self) => (
        <DeviceItem
          key={`${field.id}${index}`}
          item={field}
          index={index}
          member={`devicesGroup[${index}]`}
          change={(name, value) =>
            setValue(name, value, { shouldDirty: true, shouldValidate: true })}
          remove={remove}
          numberOfItems={self.length}
          expanded={expanded === `devicesGroup[${index}]`}
          handleSetExpanded={handleSetExpanded}
          forceError={forceError}
          projectAlerts={projectAlerts}
          projectId={projectId}
          applianceId={applianceId}
          isApplianceStop={isApplianceStop}
        />
      ))}
      {error && <Typography align="center" color="error">{error}</Typography>}
    </>
  );
};

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

/**
 * Adds alerts if the project for the warehouse was changed after alerts set
 * @param {object[]} applianceAlerts
 * @param {object} projectsAlerts
 * @param {number} projectId
 * @returns {object[]}
 */

function getUniqueAlerts(applianceAlerts = [], projectsAlerts = []) {
  const defaultAlerts = projectsAlerts.reduce((a, c) => ({ ...a, [c.id]: c }), {});
  const alerts = applianceAlerts.reduce((a, c) => {
    if (!a[c.id]) {
      const { applianceGroupId, applianceId, ...alert } = c;
      // copied from reducer
      if (alert.tMin !== undefined) alert.tMin += '';
      if (alert.tMax !== undefined) alert.tMax += '';
      if (alert.hMin !== undefined) alert.hMin += '';
      if (alert.hMax !== undefined) alert.hMax += '';
      if (alert.startDelay && alert.startDelay > 0) alert.startDelay /= 60000;
      a[c.id] = alert;
    }
    return a;
  }, { ...defaultAlerts });
  return Object.values(alerts);
}

const DevicesForm = props => {
  const {
    applianceId,
    onSubmit,
    previousStep,
    history,
    forceError,
    isApplianceStop,
    alerts,
    isDirty
  } = props;

  const dispatch = useDispatch();
  const { control, watch, formState: { isSubmitting } } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'devicesGroup' });

  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);

  const applianceForm = watch() ?? {};
  const applianceAlerts = useSelector(s => s.appliance.appliance.alerts, shallowEqual);
  const projectId = applianceForm?.warehouse?.project?.id;
  const projectsAlerts = useSelector(s => s.projectsAlerts, shallowEqual);

  const projectAlerts = useMemo(
    () => getUniqueAlerts(applianceAlerts, projectsAlerts[projectId]),
    [projectsAlerts, projectId, applianceAlerts]
  );

  const getAlertIds = useCallback(() => {
    if (applianceId && alerts?.length) {
      const uniqueIds = [...new Set(alerts.map(a => a.id))];

      return uniqueIds.join(',');
    }

    return null;
  }, [alerts, applianceId]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectAlerts(projectId, false, null, getAlertIds()));
    }
  }, [projectId, dispatch, applianceId, getAlertIds]);

  useEffect(() => {
    dispatch(fetchDevicesAvailable());
  }, [dispatch]);

  const handleCancelClick = () => {
    const path = '/warehouse';
    history.push(path);
  };

  const addDevicesItem = () => append({ id: uuidv4(), devices: [] });

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <Title variant="h4" gutterBottom>
        Devices
      </Title>
      <StyledPaper elevation={0}>
        <DeviceItems
          forceError={forceError}
          projectAlerts={projectAlerts}
          projectId={projectId}
          applianceId={applianceId}
          isApplianceStop={isApplianceStop}
          fields={fields}
          remove={remove}
        />
        <AddButtonWrapper>
          <IconButton size="small" onClick={addDevicesItem} type="button" disabled={isApplianceStop}>
            <Tooltip title="Add device group">
              <StyledAddIcon isDisabled={isApplianceStop} />
            </Tooltip>
          </IconButton>
        </AddButtonWrapper>
      </StyledPaper>
      <div ref={sentinelRef} />
      <ButtonContainer showBorder={!isIntersecting}>
        <div>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={previousStep}
          >
            Back
          </StyledButton>
          <StyledButton
            disabled={isSubmitting ||
              (applianceId ? (!isDirty || isApplianceStop) : isApplianceStop)}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </div>
      </ButtonContainer>
    </form>
  );
};

export default DevicesForm;
